import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Check, Loader2, Building2 } from "lucide-react";
import PageHero from "../components/PageHero";
import Seo from "../components/Seo";
import { Reveal } from "../lib/motion";
import { useLang } from "../i18n/LanguageContext";
import { IMAGES, CONTACT } from "../data";
import { track } from "../lib/track";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fieldCls = "w-full bg-white/[0.03] border border-white/10 rounded-2xl px-4 py-3.5 text-white placeholder:text-white/30 focus:outline-none focus:border-steel-light/60 transition-all";
const selectCls = fieldCls + " appearance-none select-chevron";

const TARGETS = [
  ["Offices", "Workspaces your team is proud to work in."],
  ["Clinics & Medical", "Hygiene-first cleaning for patient-facing spaces."],
  ["Retail", "Storefronts that make the right first impression."],
  ["Property Managers", "Common areas and multi-unit buildings, handled."],
  ["Industrial", "Warehouses and facilities kept safe and orderly."],
  ["Professional Businesses", "Reliable programs built around your hours."],
];
const FACILITY_TYPES = ["Office", "Clinic / Medical", "Retail", "Industrial / Warehouse", "Multi-Unit / Property Management", "Restaurant / Hospitality", "Other"];
const FREQS = ["Daily", "Weekly", "Bi-weekly", "Monthly", "One-time", "Custom"];

export default function Commercial() {
  const { t } = useLang();
  const [f, setF] = useState({ company: "", contact: "", phone: "", email: "", facility_type: FACILITY_TYPES[0], location: "", sqft: "", frequency: FREQS[1], locations: "1", start_date: "", requirements: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.company.trim() || !f.contact.trim() || !f.email.trim() || !f.phone.trim()) {
      toast.error(t("Please fill in company, contact name, phone and email.")); return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API}/leads`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          company_name: f.company, contact_person: f.contact, email: f.email, phone: f.phone,
          industry: f.facility_type, city: f.location, building_size: f.sqft ? `${f.sqft} sq ft` : null,
          frequency: f.frequency,
          notes: `Commercial inquiry — Locations: ${f.locations} · Desired start: ${f.start_date || "—"} · Requirements: ${f.requirements || "—"}`,
        }),
      });
      if (!res.ok) throw new Error();
      track("commercial_inquiry", { facility: f.facility_type });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch { toast.error(t("Something went wrong. Please try again.")); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="commercial-page" className="bg-ink-950">
      <Seo title="Commercial Cleaning Montreal" path="/commercial" description="Reliable commercial and office cleaning across Greater Montreal — offices, clinics, retail, industrial and property management. A cleaning partner you don't have to chase." keywords="Commercial Cleaning Montreal, Office Cleaning Montreal, Janitorial Services Montreal, Commercial Cleaning West Island" />
      <PageHero overline={t("Commercial & Facility Cleaning")} title={t("A cleaning partner you don't have to chase.")}
        subtitle={t("Reliable commercial cleaning built around your schedule, your facility and your standards.")}
        image={IMAGES.office} />

      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24 -mt-4">
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-16">
          {TARGETS.map(([title, desc], i) => (
            <Reveal key={title} delay={(i % 3) * 0.06}>
              <div data-testid={`com-target-${i}`} className="glass card-hover rounded-3xl p-6 h-full">
                <h3 className="font-600 text-white">{t(title)}</h3>
                <p className="mt-1.5 text-white/50 text-sm leading-relaxed">{t(desc)}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="grid lg:grid-cols-5 gap-8 items-start">
          <Reveal className="lg:col-span-3">
            {done ? (
              <div data-testid="commercial-success" className="glass-strong rounded-4xl p-10 text-center">
                <span className="mx-auto h-14 w-14 rounded-full bg-steel/20 flex items-center justify-center mb-5"><Check className="h-6 w-6 text-steel-light" /></span>
                <h2 className="font-serif text-gradient text-3xl mb-3" style={{ fontWeight: 500 }}>{t("Request received.")}</h2>
                <p className="text-white/60">{t("Thank you — a member of the North Peak team will contact you within one business day to discuss your facility.")}</p>
              </div>
            ) : (
              <form onSubmit={submit} noValidate data-testid="commercial-form" className="glass rounded-4xl p-6 md:p-9 space-y-4">
                <h2 className="text-white font-600 text-lg flex items-center gap-2"><Building2 className="h-5 w-5 text-steel-light" /> {t("Request a Commercial Quote")}</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <input data-testid="com-company" className={fieldCls} placeholder={t("Company")} value={f.company} onChange={(e) => set("company", e.target.value)} />
                  <input data-testid="com-contact" className={fieldCls} placeholder={t("Contact name")} value={f.contact} onChange={(e) => set("contact", e.target.value)} />
                  <input data-testid="com-phone" type="tel" className={fieldCls} placeholder={t("Phone")} value={f.phone} onChange={(e) => set("phone", e.target.value)} />
                  <input data-testid="com-email" type="email" className={fieldCls} placeholder={t("Email")} value={f.email} onChange={(e) => set("email", e.target.value)} />
                  <select data-testid="com-facility" className={selectCls} value={f.facility_type} onChange={(e) => set("facility_type", e.target.value)}>
                    {FACILITY_TYPES.map((x) => <option key={x} value={x} className="bg-ink-800">{t(x)}</option>)}
                  </select>
                  <input data-testid="com-location" className={fieldCls} placeholder={t("Location (city / borough)")} value={f.location} onChange={(e) => set("location", e.target.value)} />
                  <input data-testid="com-sqft" type="number" min="0" className={fieldCls} placeholder={t("Approx. square footage")} value={f.sqft} onChange={(e) => set("sqft", e.target.value)} inputMode="numeric" />
                  <select data-testid="com-frequency" className={selectCls} value={f.frequency} onChange={(e) => set("frequency", e.target.value)}>
                    {FREQS.map((x) => <option key={x} value={x} className="bg-ink-800">{t(x)}</option>)}
                  </select>
                  <select data-testid="com-locations" className={selectCls} value={f.locations} onChange={(e) => set("locations", e.target.value)}>
                    {["1", "2-5", "6-10", "10+"].map((x) => <option key={x} value={x} className="bg-ink-800">{x} {t("location(s)")}</option>)}
                  </select>
                  <input data-testid="com-start" type="date" className={fieldCls} value={f.start_date} onChange={(e) => set("start_date", e.target.value)} aria-label={t("Desired start date")} />
                </div>
                <textarea data-testid="com-requirements" rows={4} className={fieldCls + " resize-none"} placeholder={t("Additional requirements")} value={f.requirements} onChange={(e) => set("requirements", e.target.value)} />
                <button type="submit" data-testid="com-submit" disabled={busy}
                  className="btn-pill group w-full bg-white text-ink-950 py-4 text-sm font-600 flex items-center justify-center gap-2 hover:shadow-xl hover:shadow-white/20 disabled:opacity-60">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {t("Request a Commercial Quote")} <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </button>
                <p className="text-white/40 text-xs text-center">{t("We respond within one business day.")} · <a href={CONTACT.phoneHref} onClick={() => track("phone_click", { where: "commercial" })} className="text-steel-light">{CONTACT.phone}</a></p>
              </form>
            )}
          </Reveal>
          <Reveal delay={0.12} className="lg:col-span-2">
            <div className="glass-strong rounded-4xl p-7 md:p-9">
              <p className="overline text-steel-light mb-4">{t("What you can expect")}</p>
              <ul className="space-y-3.5">
                {["A plan built around your facility and hours", "Clear scope, clear pricing, no surprises", "Reliable scheduling and consistent teams", "Easy communication with a real person", "Insured, professional service"].map((b) => (
                  <li key={b} className="flex items-start gap-3 text-white/70 text-sm leading-relaxed">
                    <span className="h-5 w-5 rounded-full bg-steel/20 flex items-center justify-center shrink-0 mt-0.5"><Check className="h-3 w-3 text-steel-light" strokeWidth={2.5} /></span>
                    {t(b)}
                  </li>
                ))}
              </ul>
              <div className="mt-7 pt-6 border-t border-white/8">
                <p className="text-white/45 text-sm mb-3">{t("Prefer an instant estimate for a one-time commercial job?")}</p>
                <Link to="/quote" data-testid="commercial-to-calculator" className="inline-flex items-center gap-1.5 text-steel-light text-sm font-600 group">
                  {t("Use the quote calculator")} <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
